import { useQuery } from '@tanstack/react-query'
import { getStats } from '../lib/api'
import { Spinner } from '../components/ui/Spinner'
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts'

const STAT_CARDS = [
  { key: 'totalBusinesses',    label: 'Total Businesses', icon: 'ti-building-store',  from: 'from-indigo-500',  to: 'to-indigo-600' },
  { key: 'activeBusinesses',   label: 'Active',           icon: 'ti-circle-check',    from: 'from-emerald-500', to: 'to-emerald-600' },
  { key: 'featuredBusinesses', label: 'Featured',         icon: 'ti-star',            from: 'from-amber-500',   to: 'to-amber-600' },
  { key: 'sponsoredBusinesses',label: 'Sponsored',        icon: 'ti-crown',           from: 'from-purple-500',  to: 'to-purple-600' },
  { key: 'totalContacts',      label: 'Total Messages',   icon: 'ti-mail',            from: 'from-sky-500',     to: 'to-sky-600' },
  { key: 'unreadContacts',     label: 'Unread',           icon: 'ti-mail-opened',     from: 'from-rose-500',    to: 'to-rose-600' },
  { key: 'totalCategories',    label: 'Categories',       icon: 'ti-category',        from: 'from-teal-500',    to: 'to-teal-600' },
  { key: 'totalLocations',     label: 'Locations',        icon: 'ti-map-pin',         from: 'from-orange-500',  to: 'to-orange-600' },
]

// Simulated time-series data — replace with real GA data
const businessGrowthData = [
  { month: 'Jan', businesses: 12 },
  { month: 'Feb', businesses: 19 },
  { month: 'Mar', businesses: 27 },
  { month: 'Apr', businesses: 31 },
  { month: 'May', businesses: 38 },
  { month: 'Jun', businesses: 45 },
  { month: 'Jul', businesses: 52 },
]

const messageData = [
  { month: 'Jan', messages: 4 },
  { month: 'Feb', messages: 7 },
  { month: 'Mar', messages: 5 },
  { month: 'Apr', messages: 12 },
  { month: 'May', messages: 9 },
  { month: 'Jun', messages: 16 },
  { month: 'Jul', messages: 11 },
]

const PIE_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ec4899']

function StatCard({ stat, value }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden group hover:shadow-md transition-shadow">
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.from} ${stat.to} flex items-center justify-center shadow-sm`}>
            <i className={`ti ${stat.icon} text-white text-base`} />
          </div>
          <i className="ti ti-trending-up text-gray-200 text-lg group-hover:text-gray-300 transition-colors" />
        </div>
        <p className="text-2xl font-bold text-gray-900">{value ?? '—'}</p>
        <p className="text-xs text-gray-500 font-medium mt-0.5">{stat.label}</p>
      </div>
      <div className={`h-0.5 bg-gradient-to-r ${stat.from} ${stat.to}`} />
    </div>
  )
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-medium text-gray-700 mb-1">{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  )
}

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: getStats,
    staleTime: 0,        // stats always refetch when dashboard mounts
    refetchOnMount: true,
  })

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <Spinner size="lg" />
        <p className="text-sm text-gray-400">Loading dashboard...</p>
      </div>
    </div>
  )

  const pieData = data ? [
    { name: 'Active', value: data.activeBusinesses || 0 },
    { name: 'Featured', value: data.featuredBusinesses || 0 },
    { name: 'Sponsored', value: data.sponsoredBusinesses || 0 },
    { name: 'Inactive', value: (data.totalBusinesses - data.activeBusinesses) || 0 },
  ] : []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-0.5">Overview and analytics</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT_CARDS.map(stat => (
          <StatCard key={stat.key} stat={stat} value={data?.[stat.key]} />
        ))}
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Business Growth</h3>
              <p className="text-xs text-gray-400 mt-0.5">Cumulative registrations</p>
            </div>
            <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-medium">Monthly</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={businessGrowthData}>
              <defs>
                <linearGradient id="businessGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="businesses" name="Businesses" stroke="#6366f1" strokeWidth={2} fill="url(#businessGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Messages Received</h3>
              <p className="text-xs text-gray-400 mt-0.5">Contact form submissions</p>
            </div>
            <span className="text-xs bg-sky-50 text-sky-600 px-2 py-0.5 rounded-full font-medium">Monthly</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={messageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="messages" name="Messages" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Business Breakdown</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}>
                {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Quick Stats</h3>
          </div>
          <div className="space-y-3">
            {[
              { label: 'Active rate', value: data?.totalBusinesses ? Math.round((data.activeBusinesses / data.totalBusinesses) * 100) + '%' : '—', color: 'bg-emerald-500', width: data?.totalBusinesses ? (data.activeBusinesses / data.totalBusinesses) * 100 : 0 },
              { label: 'Featured rate', value: data?.totalBusinesses ? Math.round((data.featuredBusinesses / data.totalBusinesses) * 100) + '%' : '—', color: 'bg-amber-500', width: data?.totalBusinesses ? (data.featuredBusinesses / data.totalBusinesses) * 100 : 0 },
              { label: 'Unread messages', value: data ? `${data.unreadContacts} / ${data.totalContacts}` : '—', color: 'bg-rose-500', width: data?.totalContacts ? (data.unreadContacts / data.totalContacts) * 100 : 0 },
            ].map(row => (
              <div key={row.label}>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-gray-600 font-medium">{row.label}</span>
                  <span className="text-gray-900 font-bold">{row.value}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full ${row.color} rounded-full transition-all duration-500`} style={{ width: `${row.width}%` }} />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              <i className="ti ti-info-circle" />
              Charts show simulated data. Connect Google Analytics to display real traffic metrics.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
